import React from 'react'
import qs from 'qs'
import { throttle } from 'lodash'
import { useHistory, useLocation } from 'react-router'
import { useAppDispatch, useAppSelector } from '../../hooks/redux'

//import PageContent from '../../components/molecules/PageContent'
//import SidePanel from '../../components/molecules/SidePanel'
import AgentPage from '../../components/templates/AgentPage'
//import ClientChatInboxesPanel from './ClientChatInboxesPanel'
//import ClientChatPanel from './ClientChatPanel'
// import ChatHeaderStats from './ChatHeaderStats'
import ChatContainer from './ChatContainer'
import ConversationsV2 from '../../components/organisms/ConversationsV2'
import ChatWindowV2 from '../../components/organisms/ChatWindowV2'
import EngagementHubV2 from '../../components/organisms/EngagementHubV2'
import MergeContactModal from '../../components/molecules/MergeContactModal'

import useAccess from '../../hooks/useAccess'
import {
  loadUnreadCounts,
  selectConvo,
  loadAllConversations,
  loadNewConversationsList,
  setInbox,
  setTopFilter,
  checkConflicts,
  fetchOwnerTasks,
  resetLastSelected
} from '../../slices/convo'
import { useEnvironment } from '../../providers/EnvironmentProvider'
import { useSocket } from '../../providers/SocketProvider'
import { useQueueState } from '../../providers/QueueStateProvider'
import { selectAccess } from '../../slices/accessSlice'
import { selectBaseInbox } from '../../slices/baseInbox'
import { selectViews } from '../../slices/views'
import { canPullTimeline, CAN_PULL_RESULT } from './helpers'
import { useChatScroll } from '../../providers/ChatScrollProvider'
import { Routes } from '../../Routes'
import { setConversation } from '../../slices/convo'
import { getSession, updateSession } from '../../lib/api'
import { useAgent } from '../../providers/AgentProvider'
import { formatUpdateSession } from '../../components/organisms/Conversations/helpers'
import { addAlert } from '../../slices/chatBoxSlice'
import { getFormatted } from '../../utilities/phoneNumber'
import { useUserAPI } from '../../providers/UserAPIProvider'
import { useOwner } from '../../providers/OwnerProvider'
import { useTaskAPI } from '../../providers/TaskAPIProvider'
import { useReadStatus } from '../../providers/ReadStatusProvider'
import useStyles from './styles'

const ClientChats: React.FC = () => {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const history = useHistory()

  const {
    topFilter,
    inbox,
    view,
    dataFetched,
    allConversations,
    allConversationsLoaded,
    conflicts
  } = useAppSelector(selectConvo)
  const { loaded: accessLoaded } = useAppSelector(selectAccess)
  const { loaded: inboxesLoaded } = useAppSelector(selectBaseInbox)
  const { loaded: viewsLoaded } = useAppSelector(selectViews)

  const { scrollToBottom, quickScrollToBottom } = useChatScroll()
  const { joinSession } = useAgent()
  const { user, loaded: envLoaded } = useEnvironment()
  const {
    conversation,
    id: selectedConversation,
    resetSelected,
    setAccepted,
    handleSelect: handleConvoSelect
  } = useQueueState()
  const hasPermission = useAccess()
  const {
    api: {
      endSessions,
      formatConversation,
      finishFetch,
      fetchTimeline,
      fetchSingleSession
    }
  } = useSocket()
  const { allUsers, loadUser } = useUserAPI()
  const { setOwner } = useOwner()
  const { updateCacheFromSWR } = useTaskAPI()
  const { saveSessions } = useReadStatus()

  const [fetching, setFetching] = React.useState([])
  const [done, setDone] = React.useState(false)
  const [loadedUnreads, setLoadedUnreads] = React.useState(false)
  const [showMergeModal, setShowMergeModal] = React.useState(false)

  const ownerID = React.useMemo(
    () => conversation?.owner_id ?? '',
    [conversation]
  )
  const params = React.useMemo(
    () => qs.parse(location.search, { ignoreQueryPrefix: true }),
    [location.search]
  )
  const { session: querySessionId } = React.useMemo(() => params, [params])
  const [joined, setJoined] = React.useState(true)

  // Trigger join when url id changes
  React.useEffect(() => {
    if (querySessionId) {
      setJoined(false)
    }
  }, [querySessionId])

  const classes = useStyles()

  // Reset owner if id changes
  React.useEffect(() => {
    setOwner(null)
  }, [ownerID, setOwner])

  // Load user if needed
  React.useEffect(() => {
    if (ownerID) {
      loadUser(ownerID)
    }
  }, [loadUser, ownerID])

  // Update owner on users change
  React.useEffect(() => {
    const user = allUsers[ownerID]

    if (user) {
      setOwner(user)
    }
  }, [allUsers, ownerID, setOwner])

  // Check for conflicts
  React.useEffect(() => {
    dispatch(checkConflicts(ownerID))
  }, [dispatch, ownerID])

  // Loads conversations after inboxes
  React.useEffect(() => {
    if (inboxesLoaded && !allConversationsLoaded) {
      dispatch(
        loadAllConversations(store => {
          saveSessions(store)
        })
      )
    }
  }, [inboxesLoaded, allConversationsLoaded, dispatch, saveSessions])

  // Populate unread count cache from api
  React.useEffect(() => {
    if (
      user &&
      !loadedUnreads &&
      inboxesLoaded &&
      allConversationsLoaded &&
      viewsLoaded
    ) {
      dispatch(loadUnreadCounts(user.id))
      setLoadedUnreads(true)
    }
  }, [
    user,
    dispatch,
    loadedUnreads,
    inboxesLoaded,
    allConversationsLoaded,
    viewsLoaded
  ])

  React.useEffect(() => {
    return () => {
      dispatch(resetLastSelected())
    }
  }, [dispatch])

  // Closes the current session
  const handleEndConversation = React.useCallback(() => {
    endSessions([selectedConversation])
  }, [endSessions, selectedConversation])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const joinConversation = React.useCallback(
    throttle(
      async (sessionID: string) => {
        sessionStorage.setItem('viewing', sessionID)

        // Update query params so this doesn't happen again
        const newParams = { ...params }
        delete newParams.session
        history.replace(
          `${Routes.CLIENT_CHATS}${qs.stringify(newParams, {
            encode: false,
            addQueryPrefix: true
          })}`
        )
        setJoined(true)

        // Update inbox if needed
        if (['Transfers', 'Offline'].includes(topFilter)) {
          dispatch(setTopFilter('Assigned to me'))
        }

        // Switch to correct inbox
        let initialSession: LocalSession | APISession =
          allConversations[sessionID]
        if (!initialSession) {
          initialSession = await getSession(sessionID)
        }
        if (initialSession?.inbox_id !== inbox) {
          dispatch(setInbox(initialSession?.inbox_id))
        }

        // Update stored conversation
        const newConversation = formatConversation(initialSession, false)
        dispatch(
          setConversation({ id: sessionID, conversation: newConversation })
        )

        // Perform join and accept
        setAccepted(true)
        await joinSession(sessionID)
        handleConvoSelect(sessionID)

        // Update session ownership
        const addAssignedTo = formatUpdateSession(
          initialSession,
          Boolean(initialSession.ended_at)
        )
        addAssignedTo.assigned_to = user?.id
        // @ts-ignore
        await updateSession(sessionID, addAssignedTo)
      },
      1000,
      { leading: false }
    ),
    [
      params,
      dispatch,
      allConversations,
      handleConvoSelect,
      history,
      setAccepted,
      formatConversation,
      joinSession,
      user,
      inbox,
      topFilter
    ]
  )

  // Gets conversations when switching
  React.useEffect(() => {
    setDone(false)
    resetSelected()
  }, [topFilter, inbox, view, resetSelected])

  // Load list of sessions that match criteria
  React.useEffect(() => {
    if (user && !done && allConversationsLoaded) {
      dispatch(loadNewConversationsList(user.id))
    }
  }, [topFilter, inbox, view, user, dispatch, done, allConversationsLoaded])

  // Just display fetched conversations for now
  React.useEffect(() => {
    if (dataFetched && allConversationsLoaded) {
      setDone(true)
    }
  }, [dataFetched, allConversationsLoaded])

  // Fetches conversation on first selecting
  React.useEffect(() => {
    if (!accessLoaded || !envLoaded) {
      return
    }

    if (!conversation) {
      return
    }

    if (fetching.includes(selectedConversation)) {
      return
    }

    if (!conversation.fetched) {
      const selectedID = selectedConversation
      const result = canPullTimeline(hasPermission, conversation, user)

      const onComplete = () => {
        setFetching(old => old.filter(id => id !== selectedID))
        finishFetch(selectedID, false)
        scrollToBottom()
        quickScrollToBottom()
      }

      if ([CAN_PULL_RESULT.FULL, CAN_PULL_RESULT.SESSION].includes(result)) {
        setFetching(old => [...new Set([...old, selectedID])])

        if (result === CAN_PULL_RESULT.FULL) {
          fetchTimeline(selectedID, false, false).then(onComplete)
        } else {
          fetchSingleSession(selectedID).then(onComplete)
        }
      } else {
        onComplete()
      }
    }
  }, [
    selectedConversation,
    conversation,
    fetching,
    finishFetch,
    fetchTimeline,
    fetchSingleSession,
    accessLoaded,
    envLoaded,
    user,
    hasPermission,
    scrollToBottom,
    quickScrollToBottom
  ])

  // Selects the conversation from url / notification
  React.useEffect(() => {
    if (!allConversationsLoaded) {
      return
    }

    if (!querySessionId) {
      return
    }

    if (joined) {
      return
    }

    joinConversation(querySessionId as string)

    return () => {
      joinConversation.cancel()
    }
  }, [allConversationsLoaded, querySessionId, joined, joinConversation])

  const openMergeModal = React.useCallback(() => {
    setShowMergeModal(true)
  }, [])

  const hideMergeModal = React.useCallback(() => {
    setShowMergeModal(false)
  }, [])

  // Show merge message when conflicts are found
  React.useEffect(() => {
    if (!ownerID) {
      return
    }

    if (!(ownerID in conflicts)) {
      return
    }

    const ownerConflicts = conflicts[ownerID]
    if (ownerConflicts.length === 0) {
      return
    }

    const firstConflict = ownerConflicts[0]
    const firstChannel = firstConflict.channels[0]
    let value = firstChannel.value
    if (firstChannel.type !== 'email') {
      value = getFormatted(value)
    }

    const message = (
      <>
        This contact is possible to merge with{' '}
        <span className='bold'>{firstConflict.user.name}</span> ({value}).{' '}
        <span
          onClick={openMergeModal}
          className='bold'
          style={{ cursor: 'pointer', textDecoration: 'underline' }}
        >
          Merge Now
        </span>
      </>
    )

    dispatch(addAlert({ message, unique: 'merge' }))
  }, [ownerID, conflicts, dispatch, openMergeModal])

  // Fetch tasks for this owner
  React.useEffect(() => {
    if (!ownerID) {
      return
    }

    // Get tasks for this user and store in task cache
    dispatch(
      fetchOwnerTasks(ownerID, tasks => {
        if (tasks.length) {
          updateCacheFromSWR({ data: tasks })
        }
      })
    )
  }, [ownerID, dispatch, updateCacheFromSWR])

  return (
    <AgentPage
      // statsComponent={<ChatHeaderStats viewingPanel={viewingPanel} />}
      disablePadding
    >
      <div className={classes.root}>
        {/* {(!isSmall || viewingPanel) && (
          <SidePanel
            title='Conversations'
            subComponent={<ClientChatPanel />}
            toggleView={toggleView}
            bottomNavHeight={200}
            absoluteTitle
            narrow={true}
          >
            <ClientChatInboxesPanel />
          </SidePanel>
        )} */}

        <ChatContainer>
          <ConversationsV2 />

          <ChatWindowV2 endSession={handleEndConversation} isDirect={false} />

          {conversation && <EngagementHubV2 openMergeModal={openMergeModal} />}
        </ChatContainer>
      </div>

      {showMergeModal && <MergeContactModal onClose={hideMergeModal} />}
    </AgentPage>
  )
}

export default ClientChats
